<template>
  <Admin>
    <v-container fluid class="px-5">
      <v-row class="mb-4">
        <v-col>
          <h2 class="text-h4 font-weight-bold grey--text text--darken-3">Class Statistics</h2>
          <p class="text-subtitle-1 grey--text">Overview for {{ schoolClass.name || 'your class' }}</p>
        </v-col>
      </v-row>

      <!-- METRIC CARDS -->
      <v-row>
        <!-- Total Students -->
        <v-col cols="12" md="4">
          <v-card class="elevation-2" rounded="lg">
            <v-card-text class="d-flex align-center">
              <v-avatar color="info lighten-4" size="64" class="mr-4">
                <v-icon color="info" large>mdi-account-group</v-icon>
              </v-avatar>
              <div>
                <div class="text-subtitle-1 text-uppercase grey--text font-weight-bold">Total Students</div>
                <div class="text-h3 font-weight-bold text--primary">{{ totalStudents }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Parent Consents -->
        <v-col cols="12" md="4">
          <v-card class="elevation-2" rounded="lg">
            <v-card-text class="d-flex align-center">
              <v-avatar color="success lighten-4" size="64" class="mr-4">
                <v-icon color="success" large>mdi-file-check</v-icon>
              </v-avatar>
              <div>
                <div class="text-subtitle-1 text-uppercase grey--text font-weight-bold">Consents Approved</div>
                <div class="text-h3 font-weight-bold text--primary">{{ approvedConsents }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Active Skills -->
        <v-col cols="12" md="4">
          <v-card class="elevation-2" rounded="lg">
            <v-card-text class="d-flex align-center">
              <v-avatar color="primary lighten-4" size="64" class="mr-4">
                <v-icon color="primary" large>mdi-check-decagram</v-icon>
              </v-avatar>
              <div>
                <div class="text-subtitle-1 text-uppercase grey--text font-weight-bold">Active Skills</div>
                <div class="text-h3 font-weight-bold text--primary">{{ activeSkills }} / {{ totalSkills }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- VISUAL BREAKDOWN SECTION -->
      <v-row class="mt-8">
        <!-- Consent Progress Chart -->
        <v-col cols="12" md="6">
          <v-card class="elevation-2 fill-height" rounded="lg">
            <v-card-title class="pb-2">
              <v-icon left color="primary">mdi-chart-pie</v-icon> Parent Consent Progress
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="d-flex flex-column align-center justify-center pt-8 pb-8">
              <v-progress-circular
                :rotate="-90"
                :size="180"
                :width="25"
                :value="consentPercentage"
                color="success"
              >
                <div class="text-h4 font-weight-bold">{{ consentPercentage }}%</div>
              </v-progress-circular>
              <div class="mt-6 text-center text-subtitle-1 grey--text">
                <strong>{{ approvedConsents }}</strong> out of <strong>{{ totalStudents }}</strong> students have approved parent consent.
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Skills Progress Bar -->
        <v-col cols="12" md="6">
          <v-card class="elevation-2 fill-height" rounded="lg">
            <v-card-title class="pb-2">
              <v-icon left color="primary">mdi-chart-bar</v-icon> Skill Activation Rate
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pt-8">
              <div class="mb-2 d-flex justify-space-between align-end">
                <span class="text-h6 grey--text text--darken-2">Enabled Skills</span>
                <span class="text-h5 font-weight-bold primary--text">{{ skillPercentage }}%</span>
              </div>
              <v-progress-linear
                :value="skillPercentage"
                color="primary"
                height="25"
                rounded
                striped
              ></v-progress-linear>
              
              <v-list class="mt-6" dense>
                <v-list-item v-for="skill in skills" :key="skill.id">
                  <v-list-item-icon>
                    <v-icon :color="skill.enabled ? 'success' : 'grey'">
                      {{ skill.enabled ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'grey--text': !skill.enabled}">{{ skill.name }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </Admin>
</template>

<script>
import Admin from "../../layouts/Admin";
import { mapGetters, mapActions } from "vuex";

export default {
  components: {
    Admin,
  },
  computed: {
    ...mapGetters("schoolClass", ["schoolClass"]),
    ...mapGetters("skill", ["skills"]),

    totalStudents() {
      return this.schoolClass && this.schoolClass.students ? this.schoolClass.students.length : 0;
    },
    approvedConsents() {
      if (!this.schoolClass || !this.schoolClass.students) return 0;
      return this.schoolClass.students.filter((s) => s.parent_consent).length;
    },
    consentPercentage() {
      if (this.totalStudents === 0) return 0;
      return Math.round((this.approvedConsents / this.totalStudents) * 100);
    },
    totalSkills() {
      return this.skills ? this.skills.length : 0;
    },
    activeSkills() {
      if (!this.skills) return 0;
      return this.skills.filter((s) => s.enabled).length;
    },
    skillPercentage() {
      if (this.totalSkills === 0) return 0;
      return Math.round((this.activeSkills / this.totalSkills) * 100);
    }
  },
  methods: {
    ...mapActions("schoolClass", ["fetchClass"]),
    ...mapActions("skill", ["fetchSkills"]),
  },
  mounted() {
    this.fetchClass();
    this.fetchSkills();
  }
};
</script>